import { resolutionCapLevel, resolutionCeiling } from "@productlane/lib"

const USAGE_PATH = "/settings/ai/usage"
// A trial cannot buy overage, so its only way out of the cap is a payment method.
const PLANS_PATH = "/settings/plans"

interface AgentChannels {
	isEnabled?: boolean
	aiAutoRepliesEnabled?: boolean
	deploySlack?: boolean
	deployTeams?: boolean
	deployApi?: boolean
	deploySupportPortal?: boolean
}

export function agentDeployed(settings: AgentChannels | null | undefined) {
	if (!settings) return false
	return !!(
		settings.isEnabled ||
		settings.aiAutoRepliesEnabled ||
		settings.deploySlack ||
		settings.deployTeams ||
		settings.deployApi ||
		settings.deploySupportPortal
	)
}

export interface ResolutionCapWarning {
	level: 80 | 100
	heading: string
	countLabel: string
	// Share of the ceiling used, clamped to 1 for a cap lowered below usage.
	progress: number
	action: string
	path: string
	dismissKey: string
}

const count = (value: number) => value.toLocaleString("en-US")

function sidebarCapAction({
	trialing,
	payPerUseEnabled,
}: {
	trialing: boolean
	payPerUseEnabled: boolean
}): string {
	if (trialing) return "Add payment details"
	return payPerUseEnabled ? "Raise cap" : "Enable pay-per-use"
}

// A dismissal holds for one level in one reset period, so the step from 80 to
// 100 and next month's cap both show again.
export function resolutionCapDismissKey({
	workspaceId,
	level,
	trialing,
	now,
}: {
	workspaceId: string
	level: 80 | 100
	trialing: boolean
	now: Date
}): string {
	if (trialing) return `${workspaceId}:${level}:trial`
	const month = String(now.getUTCMonth() + 1).padStart(2, "0")
	return `${workspaceId}:${level}:${now.getUTCFullYear()}-${month}`
}

export function resolutionCapWarning({
	workspaceId,
	used,
	payPerUseEnabled,
	monthlyLimit,
	freeLimit,
	trialing,
	now = new Date(),
}: {
	workspaceId: string
	used: number
	payPerUseEnabled: boolean
	monthlyLimit: number | null
	freeLimit: number
	trialing: boolean
	now?: Date
}): ResolutionCapWarning | null {
	const ceiling = resolutionCeiling({
		freeLimit,
		trialing,
		payPerUseEnabled,
		monthlyLimit,
	})
	const level = resolutionCapLevel({ used, ceiling })
	if (level === 0 || ceiling === null) return null
	return {
		level,
		heading: level === 100 ? "AI agent stopped" : "AI resolutions at 80%",
		countLabel: `${count(used)} of ${count(ceiling)} used`,
		progress: Math.min(used / ceiling, 1),
		action: sidebarCapAction({ trialing, payPerUseEnabled }),
		path: trialing ? PLANS_PATH : USAGE_PATH,
		dismissKey: resolutionCapDismissKey({ workspaceId, level, trialing, now }),
	}
}

export interface SidebarCapDisplay {
	card: boolean
	// The expanded sidebar keeps a mark once the card is closed, but only while
	// the agent is actually stopped.
	icon: boolean
	collapsedIcon: boolean
}

export function sidebarCapDisplay(
	warning: ResolutionCapWarning | null,
	dismissedKey: string | null,
): SidebarCapDisplay {
	if (!warning) return { card: false, icon: false, collapsedIcon: false }
	const dismissed = dismissedKey === warning.dismissKey
	const stopped = warning.level === 100
	return {
		card: !dismissed,
		icon: dismissed && stopped,
		collapsedIcon: stopped || !dismissed,
	}
}
